import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { toast } from 'sonner';
import { uintCV, principalCV, noneCV } from '@stacks/transactions';
import AdminTabsNav, { useAdminTabFromUrl } from '@/components/AdminTabsNav';
import { 
  Wallet, 
  ArrowUpRight,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  Users,
  Bot,
  MessageSquare,
  TrendingUp,
  Activity,
  DollarSign,
  PieChart,
  Calendar,
  Wrench,
  Search,
  Shield,
  Eye,
  Globe,
  Cpu,
  Zap,
  Check,
  X,
  Coins,
  Droplets,
  Trophy,
  Target,
  ShieldAlert
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const STXOSHI_LOGO = process.env.REACT_APP_LOGO_URL;

// Treasury wallet - ONLY this wallet can process STXOSHI withdrawals and swaps
const TREASURY_WALLET = 'SP3VVPEFZNN84EGMD0GN4XWAZ4X5ZMWC852NGWVWN';

// Admin addresses - these addresses can access admin features
const ADMIN_ADDRESSES = [
  'SP3VVPEFZNN84EGMD0GN4XWAZ4X5ZMWC852NGWVWN',  // Treasury/Hot wallet (VWN)
  'SPD4KEYK7HDNED4GWFWRB1V405XD0CB6EN124BCG',  // Admin (BCG)
  'SP17175BTCCNHF06Z7TNP3PG9FET3JRFQ85C31WSY',  // Admin 2 (WSY)
  'SP1WST899N8B2VR76DCHQ56ZQ78R9VSYT2B2RNTJY',  // Admin 3
  'SP2R3H4VQEHPAV8VM8KS0JR8BAS8HTANZ92R64H8K',  // Admin 4 (H8K)
];

// Stats Card Component
const StatCard = ({ icon: Icon, label, value, subValue, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    green: "bg-green-500/10 text-green-400 border-green-500/30",
    yellow: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
    purple: "bg-purple-500/10 text-purple-400 border-purple-500/30",
    orange: "bg-orange-500/10 text-orange-400 border-orange-500/30",
    pink: "bg-pink-500/10 text-pink-400 border-pink-500/30",
  };
  
  return (
    <div className={`rounded-xl border p-4 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      {subValue && <div className="text-xs mt-1 opacity-70">{subValue}</div>}
    </div>
  );
};

export default function AdminWithdrawals() {
  const { addresses, signStxTransfer, signContractCall, isConnected, getAuthHeaders } = useWallet();
  const [withdrawals, setWithdrawals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [stats, setStats] = useState(null);
  const [flipStats, setFlipStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [activeTab, setActiveTab] = useState(() => {
    // Initialize from ?tab=… URL param so deep-links from /admin/orphans work
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tab');
    return ['stats', 'withdrawals', 'recovery', 'swaps'].includes(t) ? t : 'stats';
  });
  const navigate = useNavigate();
  const location = useLocation();
  // Sync URL param when the user clicks a tab inside this page — so refreshes
  // and shareable links land on the same tab.
  const urlTab = useAdminTabFromUrl('stats');
  useEffect(() => {
    if (urlTab !== activeTab) {
      navigate(`/admin/withdrawals?tab=${activeTab}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  
  // Recovery tool state
  const [recoveryTxid, setRecoveryTxid] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [txVerification, setTxVerification] = useState(null);
  const [lookupAddress, setLookupAddress] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [manualAgentId, setManualAgentId] = useState(''); // Manual agent ID entry
  const [recoveryAmount, setRecoveryAmount] = useState('');
  const [recoveryNotes, setRecoveryNotes] = useState('');
  const [extraYield, setExtraYield] = useState(''); // Extra yield for compensation (can be negative)
  const [recoveryProtocol, setRecoveryProtocol] = useState('stxoshi'); // Protocol selector
  
  // Update bonus yield state
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [bonusYieldAmount, setBonusYieldAmount] = useState('');
  const [bonusUpdateLoading, setBonusUpdateLoading] = useState(false);
  
  // Swap fulfillment state
  const [swaps, setSwaps] = useState([]);
  const [swapStats, setSwapStats] = useState(null);
  const [hotWalletBalance, setHotWalletBalance] = useState(null);
  const [swapsLoading, setSwapsLoading] = useState(false);
  const [swapFulfilling, setSwapFulfilling] = useState(null);
  const [swapFilter, setSwapFilter] = useState('deposited');
  
  // Abandoned swaps state
  const [abandonedSwaps, setAbandonedSwaps] = useState(null);
  const [abandonedLoading, setAbandonedLoading] = useState(false);
  const [cancellingAbandoned, setCancellingAbandoned] = useState(false);
  
  // Available protocols for recovery
  const PROTOCOLS = [
    { value: 'stxoshi', label: 'STXOSHI Vault', asset: 'stxoshi', apy: '25%' },
    { value: 'stackingdao', label: 'StackingDAO', asset: 'stx', apy: '~5%' },
    { value: 'zest', label: 'Zest Protocol', asset: 'stx', apy: '~8%' },
    { value: 'hermetica', label: 'Hermetica', asset: 'stx', apy: '~6%' },
    { value: 'alex', label: 'ALEX DEX', asset: 'alex', apy: '~12%' },
    { value: 'bitflow', label: 'Bitflow', asset: 'stx', apy: '~5%' },
    { value: 'velar', label: 'Velar', asset: 'velar', apy: '~10%' },
  ];
  
  const isAdmin = ADMIN_ADDRESSES.includes(addresses?.stx);
  const isTreasury = addresses?.stx === TREASURY_WALLET;

  const fetchStats = useCallback(async () => {
    if (!isAdmin) return;

    // Don't show loading spinner if we already have stats (just refresh in background)
    // NOTE: previously this useCallback listed `stats` in its deps, which created
    // a render loop: setStats → stats changes → fetchStats reidentifies →
    // useEffect re-runs → setStats → ... fetching every render (felt like
    // "every second" auto-refresh). Now we read `stats` via the setter form
    // and keep the dep array stable.
    setStats((current) => {
      if (!current) setStatsLoading(true);
      return current;
    });
    
    try {
      // Fetch both stats in parallel with timeouts
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
      
      const [statsResponse, flipResponse] = await Promise.all([
        fetch(`${API}/admin/stats`, {
          headers: { 'X-STX-Address': addresses.stx },
          signal: controller.signal
        }).catch(() => null),
        fetch(`${API}/flip-market/admin-stats`, {
          signal: controller.signal
        }).catch(() => null)
      ]);
      
      clearTimeout(timeoutId);
      
      if (statsResponse?.ok) {
        const data = await statsResponse.json();
        setStats(data);
      }
      
      if (flipResponse?.ok) {
        const flipData = await flipResponse.json();
        if (flipData.success) {
          setFlipStats(flipData.data);
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching stats:', error);
      }
    } finally {
      setStatsLoading(false);
    }
  }, [isAdmin, addresses?.stx]);

  const fetchWithdrawals = useCallback(async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/withdrawals?status=${filter}`, {
        headers: { 'X-STX-Address': addresses.stx }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWithdrawals(data.withdrawals || []);
        setSummary(data.summary || null);
      } else {
        const error = await response.json();
        toast.error('Failed to load withdrawals', { description: error.detail });
      }
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      toast.error('Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, addresses?.stx, filter]);

  // Initial-load only — fires ONCE when admin tab first opens. Subsequent
  // refreshes happen via the explicit "Refresh" button. Filter changes
  // trigger their own fetch below.
  // (Previously this depended on [fetchWithdrawals, fetchStats], which
  // reidentified on every state change and created a fetch-every-render loop.)
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (!isAdmin || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchWithdrawals();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Re-fetch withdrawals when the user explicitly changes the filter chip.
  // Skip on the very first mount (the effect above handles it).
  useEffect(() => {
    if (!hasFetchedRef.current) return;
    fetchWithdrawals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Swap Fulfillment Functions
  const fetchSwaps = useCallback(async () => {
    if (!isAdmin) return;
    
    setSwapsLoading(true);
    try {
      const [swapsRes, balanceRes] = await Promise.all([
        fetch(`${API}/admin/swaps?status=${swapFilter}`, {
          headers: { 'X-STX-Address': addresses.stx }
        }),
        fetch(`${API}/admin/hot-wallet/balance`, {
          headers: { 'X-STX-Address': addresses.stx }
        })
      ]);
      
      if (swapsRes.ok) {
        const data = await swapsRes.json();
        setSwaps(data.swaps || []);
        setSwapStats(data.stats || null);
      }
      
      if (balanceRes.ok) {
        const balanceData = await balanceRes.json();
        setHotWalletBalance(balanceData);
      }
    } catch (error) {
      console.error('Error fetching swaps:', error);
      toast.error('Failed to load swaps');
    } finally {
      setSwapsLoading(false);
    }
  }, [isAdmin, addresses?.stx, swapFilter]);

  // Fetch swaps once when the swaps tab opens, and re-fetch when the user
  // changes the swap status filter. NOT on every fetchSwaps reidentification
  // (which would loop because fetchSwaps depends on state that fetchSwaps sets).
  const swapsHasFetchedRef = useRef(false);
  useEffect(() => {
    if (activeTab !== 'swaps') return;
    if (!swapsHasFetchedRef.current) {
      swapsHasFetchedRef.current = true;
    }
    fetchSwaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, swapFilter]);

  const fulfillSwap = async (swapId, auto = true) => {
    setSwapFulfilling(swapId);
    try {
      const endpoint = auto 
        ? `${API}/admin/swaps/${swapId}/auto-fulfill`
        : `${API}/admin/swaps/${swapId}/fulfill`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-STX-Address': addresses.stx 
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Swap fulfilled!', {
          description: `TX: ${data.payout_txid?.slice(0, 16)}...`,
        });
        fetchSwaps();
      } else {
        toast.error('Fulfillment failed', { description: data.detail || data.error });
      }
    } catch (error) {
      console.error('Error fulfilling swap:', error);
      toast.error('Failed to fulfill swap');
    } finally {
      setSwapFulfilling(null);
    }
  };

  const fulfillAllSwaps = async () => {
    if (!confirm('Are you sure you want to fulfill ALL pending swaps? This will send STXOSHI to all deposited users.')) {
      return;
    }
    
    setSwapFulfilling('all');
    try {
      const response = await fetch(`${API}/admin/swaps/auto-fulfill-all`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-STX-Address': addresses.stx 
        }
      });
      
      const data = await response.json();
      
      toast.success(`Batch fulfillment complete`, {
        description: `${data.successful} successful, ${data.failed} failed`,
      });
      fetchSwaps();
    } catch (error) {
      console.error('Error in batch fulfillment:', error);
      toast.error('Batch fulfillment failed');
    } finally {
      setSwapFulfilling(null);
    }
  };

  const markSwapComplete = async (swapId, stxoshiAmount) => {
    const note = prompt(`Mark as complete (already paid)?\n\nOptionally enter the payout txid if you have it:`);
    if (note === null) return; // User cancelled
    
    setSwapFulfilling(swapId);
    try {
      const params = new URLSearchParams({ note: note || 'Already paid manually' });
      if (note && note.length === 64) {
        params.set('payout_txid', note);
        params.set('note', 'Manually fulfilled');
      }
      
      const response = await fetch(`${API}/admin/swaps/${swapId}/mark-complete?${params}`, {
        method: 'POST',
        headers: { 'X-STX-Address': addresses.stx }
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Swap marked as complete');
        fetchSwaps();
      } else {
        toast.error(data.detail || 'Failed to mark complete');
      }
    } catch (error) {
      toast.error('Failed to mark complete');
    } finally {
      setSwapFulfilling(null);
    }
  };

  const cancelSwap = async (swapId) => {
    const reason = prompt('Cancel this swap? Enter reason (e.g., "User never deposited", "Duplicate entry"):');
    if (!reason) return;
    
    setSwapFulfilling(swapId);
    try {
      const response = await fetch(`${API}/admin/swaps/${swapId}/cancel?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: { 'X-STX-Address': addresses.stx }
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Swap cancelled');
        fetchSwaps();
      } else {
        toast.error(data.detail || 'Failed to cancel');
      }
    } catch (error) {
      toast.error('Failed to cancel swap');
    } finally {
      setSwapFulfilling(null);
    }
  };

  // Abandoned Swaps Functions
  const fetchAbandonedSwaps = async () => {
    setAbandonedLoading(true);
    try {
      const response = await fetch(`${API}/admin/swaps/abandoned?older_than_hours=24`, {
        headers: { 'X-STX-Address': addresses.stx }
      });
      const data = await response.json();
      if (data.success) {
        setAbandonedSwaps(data);
      } else {
        toast.error('Failed to fetch abandoned swaps');
      }
    } catch (error) {
      toast.error('Failed to fetch abandoned swaps');
    } finally {
      setAbandonedLoading(false);
    }
  };

  const cancelAbandonedSwaps = async () => {
    if (!confirm(`Are you sure you want to cancel ${abandonedSwaps?.count || 0} abandoned pending swaps?\n\nThis will cancel all swaps with status "pending" (never deposited) that are older than 24 hours.`)) {
      return;
    }
    
    setCancellingAbandoned(true);
    try {
      const response = await fetch(`${API}/admin/swaps/cancel-abandoned?older_than_hours=24`, {
        method: 'POST',
        headers: { 'X-STX-Address': addresses.stx }
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Cancelled ${data.cancelled_count} abandoned swaps`);
        setAbandonedSwaps(null);
        fetchSwaps();
      } else {
        toast.error(data.detail || 'Failed to cancel abandoned swaps');
      }
    } catch (error) {
      toast.error('Failed to cancel abandoned swaps');
    } finally {
      setCancellingAbandoned(false);
    }
  };

  // Recovery Tool Functions
  const verifyTransaction = async () => {
    if (!recoveryTxid.trim()) {
      toast.error('Please enter a transaction ID');
      return;
    }
    
    setRecoveryLoading(true);
    setTxVerification(null);
    
    try {
      const response = await fetch(`${API}/admin/verify-tx/${recoveryTxid.trim()}`, {
        headers: { 'X-STX-Address': addresses.stx }
      });
      
      const data = await response.json();
      setTxVerification(data);
      
      if (data.found && data.status === 'success') {
        toast.success('Transaction verified on-chain!');
      } else if (data.found) {
        toast.warning(`Transaction found but status: ${data.status}`);
      } else {
        toast.error('Transaction not found on-chain');
      }
    } catch (error) {
      console.error('Verify error:', error);
      toast.error('Failed to verify transaction');
    } finally {
      setRecoveryLoading(false);
    }
  };

  const lookupAgent = async () => {
    if (!lookupAddress.trim()) {
      toast.error('Please enter a wallet address');
      return;
    }
    
    setLookupLoading(true);
    setLookupResult(null);
    setSelectedAgent(null);
    
    try {
      const response = await fetch(`${API}/admin/lookup-agent?stx_address=${lookupAddress.trim()}`, {
        headers: { 'X-STX-Address': addresses.stx }
      });
      
      const data = await response.json();
      setLookupResult(data);
      
      if (data.found) {
        toast.success(`Found ${data.agents_count} agent(s) for this wallet`);
      } else {
        toast.error(data.message || 'No agents found');
      }
    } catch (error) {
      console.error('Lookup error:', error);
      toast.error('Failed to look up wallet');
    } finally {
      setLookupLoading(false);
    }
  };

  const executeRecovery = async () => {
    if (!recoveryTxid.trim()) {
      toast.error('Please enter a transaction ID');
      return;
    }
    if (!selectedAgent) {
      toast.error('Please select an agent');
      return;
    }
    if (!recoveryAmount || parseFloat(recoveryAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setRecoveryLoading(true);
    
    const microAmount = Math.floor(parseFloat(recoveryAmount) * 1_000_000); // 6 decimals
    const extraYieldMicro = extraYield ? Math.floor(parseFloat(extraYield) * 1_000_000) : 0;
    
    // Get the selected protocol's asset
    const selectedProtocolConfig = PROTOCOLS.find(p => p.value === recoveryProtocol);
    const asset = selectedProtocolConfig?.asset || recoveryProtocol;
    
    // Use XMLHttpRequest for better Safari/iOS compatibility
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/admin/recover-deposit`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('X-STX-Address', addresses.stx);
    
    xhr.onload = function() {
      setRecoveryLoading(false);
      try {
        const data = JSON.parse(xhr.responseText);
        
        if (xhr.status >= 200 && xhr.status < 300 && data.success) {
          const bonusMsg = extraYieldMicro !== 0 ? ` (${extraYieldMicro > 0 ? '+' : ''}${extraYield} yield adjustment)` : '';
          toast.success('Deposit recovered successfully!', {
            description: `${selectedProtocolConfig?.label}: ${data.position_id?.slice(0, 8)}...${bonusMsg}`
          });
          
          // Reset form
          setRecoveryTxid('');
          setTxVerification(null);
          setLookupAddress('');
          setLookupResult(null);
          setSelectedAgent(null);
          setManualAgentId('');
          setRecoveryAmount('');
          setRecoveryNotes('');
          setExtraYield('');
          setRecoveryProtocol('stxoshi');
        } else {
          // Extract error message properly
          let errorMsg = 'Unknown error';
          if (typeof data.detail === 'string') {
            errorMsg = data.detail;
          } else if (Array.isArray(data.detail) && data.detail[0]?.msg) {
            errorMsg = data.detail[0].msg;
          } else if (data.message) {
            errorMsg = data.message;
          }
          
          // Check for duplicate txid error
          if (errorMsg.includes('Position already exists')) {
            toast.error('Position already exists!', { 
              description: 'This transaction was already recovered.',
              duration: 8000
            });
          } else {
            toast.error('Recovery failed', { description: errorMsg });
          }
        }
      } catch (parseErr) {
        console.error('Parse error:', parseErr, 'Response:', xhr.responseText);
        toast.error('Recovery failed', { description: xhr.responseText || 'Server error' });
      }
    };
    
    xhr.onerror = function() {
      setRecoveryLoading(false);
      toast.error('Recovery failed', { description: 'Network error. Please try again.' });
    };
    
    xhr.ontimeout = function() {
      setRecoveryLoading(false);
      toast.error('Recovery failed', { description: 'Request timed out.' });
    };
    
    xhr.timeout = 30000;
    
    try {
      xhr.send(JSON.stringify({
        txid: recoveryTxid.trim(),
        agent_id: selectedAgent.id,
        protocol: recoveryProtocol,
        asset: asset,
        amount: microAmount,
        user_stx_address: lookupResult?.user?.stx_address || lookupAddress,
        notes: recoveryNotes || `Admin recovery on ${new Date().toISOString()}`,
        extra_yield: extraYieldMicro,
        use_original_timestamp: true
      }));
    } catch (sendErr) {
      setRecoveryLoading(false);
      toast.error('Recovery failed', { description: sendErr.message });
    }
  };

  // Update bonus yield on existing position
  const updateBonusYield = async () => {
    if (!selectedPosition) {
      toast.error('Please select a position');
      return;
    }
    if (!bonusYieldAmount || parseFloat(bonusYieldAmount) <= 0) {
      toast.error('Please enter a valid bonus amount');
      return;
    }
    
    setBonusUpdateLoading(true);
    
    try {
      const microAmount = Math.floor(parseFloat(bonusYieldAmount) * 1_000_000);
      
      const response = await fetch(`${API}/admin/update-bonus-yield`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-STX-Address': addresses.stx 
        },
        body: JSON.stringify({
          position_id: selectedPosition.id,
          extra_yield: microAmount,
          notes: `Added ${bonusYieldAmount} bonus yield`
        })
      });
      
      const responseClone = response.clone();
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        const text = await responseClone.text();
        throw new Error(`Server error: ${text || 'Unknown error'}`);
      }
      
      if (response.ok && data.success) {
        toast.success('Bonus yield updated!', {
          description: `Added ${bonusYieldAmount} STXOSHI. Total bonus: ${(data.new_total_bonus / 1_000_000).toLocaleString()}`
        });
        
        // Refresh the lookup to show updated position
        if (lookupAddress) {
          lookupAgent();
        }
        
        setBonusYieldAmount('');
        setSelectedPosition(null);
      } else {
        toast.error('Update failed', { description: data.detail || 'Unknown error' });
      }
    } catch (error) {
      console.error('Bonus update error:', error);
      toast.error('Failed to update bonus yield', { description: error.message });
    } finally {
      setBonusUpdateLoading(false);
    }
  };

  const handleProcessWithdrawal = async (withdrawal) => {
    // Must be connected with treasury wallet to sign STXOSHI token transfers
    if (!isTreasury) {
      toast.error('Treasury wallet required', {
        description: `Connect with ${TREASURY_WALLET.slice(0, 10)}... (VWN) to process STXOSHI withdrawals`,
        duration: 8000
      });
      return;
    }
    
    if (!signContractCall) {
      toast.error('Wallet not connected');
      return;
    }
    
    setProcessing(withdrawal.id);
    
    try {
      // Step 1: Prepare the withdrawal
      const prepareResponse = await fetch(
        `${API}/admin/withdrawals/${withdrawal.id}/prepare`,
        {
          method: 'POST',
          headers: { 'X-STX-Address': addresses.stx }
        }
      );
      
      const prepareData = await prepareResponse.json();
      
      if (!prepareResponse.ok) {
        throw new Error(prepareData.detail || 'Failed to prepare withdrawal');
      }
      
      const tx = prepareData.transaction;
      
      // Check if it's a STXOSHI token transfer (contract call) or legacy STX transfer
      if (tx.type === 'contract_call') {
        // STXOSHI Token Transfer via SIP-010 contract
        toast.info(`Sending ${prepareData.amount_stxoshi.toFixed(6)} STXOSHI tokens to ${withdrawal.user_stx_address.slice(0, 10)}...`);
        
        // Build SIP-010 transfer function args
        // transfer(amount uint, sender principal, recipient principal, memo (optional buff))
        const functionArgs = [
          uintCV(tx.functionArgs.amount),
          principalCV(tx.functionArgs.sender),
          principalCV(tx.functionArgs.recipient),
          noneCV()  // No memo
        ];
        
        const txResult = await signContractCall({
          contractAddress: tx.contractAddress,
          contractName: tx.contractName,
          functionName: tx.functionName,
          functionArgs: functionArgs,
          postConditionMode: 'allow'  // Allow for token transfers
        });
        
        if (txResult && txResult.success && txResult.txid) {
          // Step 3: Mark as complete
          const completeResponse = await fetch(
            `${API}/admin/withdrawals/${withdrawal.id}/complete?txid=${txResult.txid}`,
            {
              method: 'POST',
              headers: { 'X-STX-Address': addresses.stx }
            }
          );
          
          if (completeResponse.ok) {
            toast.success('STXOSHI tokens sent!', {
              description: `TX: ${txResult.txid.slice(0, 16)}...`
            });
            fetchWithdrawals();
          } else {
            toast.warning('Transaction sent but status update failed', {
              description: `TX: ${txResult.txid.slice(0, 16)}...`
            });
          }
        } else {
          throw new Error('Transaction was not successful');
        }
      } else {
        // Legacy STX transfer (fallback for old withdrawals)
        toast.info(`Sending ${prepareData.amount_stx?.toFixed(6) || prepareData.amount_stxoshi?.toFixed(6)} to ${withdrawal.user_stx_address.slice(0, 10)}...`);
        
        const txResult = await signStxTransfer({
          recipient: tx.recipient,
          amount: tx.amount,
          memo: tx.memo || ''
        });
        
        if (txResult && txResult.success && txResult.txid) {
          const completeResponse = await fetch(
            `${API}/admin/withdrawals/${withdrawal.id}/complete?txid=${txResult.txid}`,
            {
              method: 'POST',
              headers: { 'X-STX-Address': addresses.stx }
            }
          );
          
          if (completeResponse.ok) {
            toast.success('Withdrawal processed!', {
              description: `TX: ${txResult.txid.slice(0, 16)}...`
            });
            fetchWithdrawals();
          } else {
            toast.warning('Transaction sent but status update failed', {
              description: `TX: ${txResult.txid.slice(0, 16)}...`
            });
          }
        } else {
          throw new Error('Transaction was not successful');
        }
      }
    } catch (error) {
      console.error('Process withdrawal error:', error);
      toast.error('Failed to process withdrawal', { description: error.message });
    } finally {
      setProcessing(null);
    }
  };

  // Auto-fulfill withdrawal using Python backend (no wallet signing required!)
  const handleAutoFulfillWithdrawal = async (withdrawal) => {
    setProcessing(withdrawal.id);
    
    try {
      const userAddress = withdrawal.user_stx_address || withdrawal.user_address || withdrawal.to_wallet || 'Unknown';
      toast.info(`Auto-fulfilling ${(withdrawal.amount / 1_000_000).toFixed(6)} STXOSHI to ${userAddress.slice(0, 10)}...`);
      
      const response = await fetch(
        `${API}/admin/withdrawals/${withdrawal.id}/auto-fulfill`,
        {
          method: 'POST',
          headers: { 'X-STX-Address': addresses.stx }
        }
      );
      
      // Clone the response to read it multiple times if needed
      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        throw new Error(`Server error: ${responseText.slice(0, 200)}`);
      }
      
      if (response.ok && data.success) {
        toast.success('Withdrawal auto-fulfilled!', {
          description: `TX: ${data.txid.slice(0, 16)}...`,
          action: {
            label: 'View TX',
            onClick: () => window.open(data.explorer_url, '_blank')
          }
        });
        fetchWithdrawals();
      } else {
        throw new Error(data.detail || data.error || 'Failed to auto-fulfill');
      }
    } catch (error) {
      console.error('Auto-fulfill withdrawal error:', error);
      toast.error('Auto-fulfill failed', { description: error.message });
    } finally {
      setProcessing(null);
    }
  };

  // Auto-fulfill ALL pending withdrawals
  const handleAutoFulfillAllWithdrawals = async () => {
    const pendingCount = withdrawals.filter(w => w.status === 'pending').length;
    if (pendingCount === 0) {
      toast.info('No pending withdrawals to process');
      return;
    }
    
    if (!confirm(`Auto-fulfill ${pendingCount} pending withdrawal(s)?\n\nThis will send STXOSHI tokens automatically.`)) {
      return;
    }
    
    setProcessing('all');
    
    try {
      toast.info(`Auto-fulfilling ${pendingCount} pending withdrawals...`);
      
      const response = await fetch(
        `${API}/admin/withdrawals/auto-fulfill-all`,
        {
          method: 'POST',
          headers: { 'X-STX-Address': addresses.stx }
        }
      );
      
      // Clone the response to read it multiple times if needed
      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        throw new Error(`Server error: ${responseText.slice(0, 200)}`);
      }
      
      if (response.ok && data.success) {
        if (data.successful > 0) {
          toast.success(`${data.successful} withdrawal(s) fulfilled!`);
        }
        if (data.failed > 0) {
          toast.warning(`${data.failed} withdrawal(s) failed`);
        }
        fetchWithdrawals();
      } else {
        throw new Error(data.detail || data.error || 'Failed to auto-fulfill');
      }
    } catch (error) {
      console.error('Auto-fulfill all error:', error);
      toast.error('Auto-fulfill failed', { description: error.message });
    } finally {
      setProcessing(null);
    }
  };

  // Not admin view
  if (!isConnected) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Wallet className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Connect Wallet</h2>
          <p className="text-zinc-400">Connect your wallet to access admin features</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-zinc-400">Only admin addresses can access this page</p>
          <p className="text-xs text-zinc-600 mt-2">Connected: {addresses?.stx?.slice(0, 10)}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex-shrink-0 hover:opacity-80 transition-opacity" data-testid="admin-logo-link">
              <img 
                src={STXOSHI_LOGO} 
                alt="STXOSHI" 
                className="w-12 h-12 rounded-full"
              />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-zinc-400">App statistics and withdrawal management</p>
            </div>
          </div>
          <button
            onClick={() => { fetchWithdrawals(); fetchStats(); }}
            disabled={loading || statsLoading}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-white"
          >
            <RefreshCw className={`w-4 h-4 ${(loading || statsLoading) ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <AdminTabsNav
          active={activeTab}
          onTabChange={setActiveTab}
          pendingCount={summary?.pending_count || 0}
        />

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div className="space-y-6">
            {statsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : stats ? (
              <>
                {/* Website Traffic Section - At Top */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-cyan-400" />
                    Website Traffic
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Eye className="w-4 h-4 text-cyan-400" />
                        <span className="text-xs font-medium text-cyan-400">Total Hits (All-Time)</span>
                      </div>
                      <div className="text-2xl font-bold text-white">{(stats.traffic?.total || 0).toLocaleString()}</div>
                      <div className="text-xs mt-1 text-cyan-300/70">{(stats.traffic?.unique_visitors_total || 0).toLocaleString()} unique visitors</div>
                    </div>
                    <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Eye className="w-4 h-4 text-cyan-400" />
                        <span className="text-xs font-medium text-cyan-400">Hits Today</span>
                      </div>
                      <div className="text-2xl font-bold text-white">{(stats.traffic?.today || 0).toLocaleString()}</div>
                      <div className="text-xs mt-1 text-cyan-300/70">{(stats.traffic?.unique_visitors_today || 0).toLocaleString()} unique visitors</div>
                    </div>
                    <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Eye className="w-4 h-4 text-cyan-400" />
                        <span className="text-xs font-medium text-cyan-400">Hits This Week</span>
                      </div>
                      <div className="text-2xl font-bold text-white">{(stats.traffic?.this_week || 0).toLocaleString()}</div>
                      <div className="text-xs mt-1 text-cyan-300/70">{(stats.traffic?.unique_visitors_week || 0).toLocaleString()} unique visitors</div>
                    </div>
                    <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Eye className="w-4 h-4 text-cyan-400" />
                        <span className="text-xs font-medium text-cyan-400">Hits This Month</span>
                      </div>
                      <div className="text-2xl font-bold text-white">{(stats.traffic?.this_month || 0).toLocaleString()}</div>
                      <div className="text-xs mt-1 text-cyan-300/70">{(stats.traffic?.unique_visitors_month || 0).toLocaleString()} unique visitors</div>
                    </div>
                  </div>
                </div>

                {/* M2M / Autonomous Agent Traffic Section */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Cpu className="w-5 h-5 text-purple-400" />
                    M2M / Autonomous Agent Traffic
                    <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full ml-2">AI Agents</span>
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Cpu className="w-4 h-4 text-purple-400" />
                        <span className="text-xs font-medium text-purple-400">M2M Total (All-Time)</span>
                      </div>
                      <div className="text-2xl font-bold text-white">{(stats.m2m_traffic?.total || 0).toLocaleString()}</div>
                      <div className="text-xs mt-1 text-purple-300/70">{(stats.m2m_traffic?.unique_agents_total || 0).toLocaleString()} unique agents</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-purple-400" />
                        <span className="text-xs font-medium text-purple-400">M2M Today</span>
                      </div>
                      <div className="text-2xl font-bold text-white">{(stats.m2m_traffic?.today || 0).toLocaleString()}</div>
                      <div className="text-xs mt-1 text-purple-300/70">{(stats.m2m_traffic?.unique_agents_today || 0).toLocaleString()} unique agents</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-purple-400" />
                        <span className="text-xs font-medium text-purple-400">M2M This Week</span>
                      </div>
                      <div className="text-2xl font-bold text-white">{(stats.m2m_traffic?.this_week || 0).toLocaleString()}</div>
                      <div className="text-xs mt-1 text-purple-300/70">{(stats.m2m_traffic?.unique_agents_week || 0).toLocaleString()} unique agents</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-purple-400" />
                        <span className="text-xs font-medium text-purple-400">M2M This Month</span>
                      </div>
                      <div className="text-2xl font-bold text-white">{(stats.m2m_traffic?.this_month || 0).toLocaleString()}</div>
                      <div className="text-xs mt-1 text-purple-300/70">{(stats.m2m_traffic?.unique_agents_month || 0).toLocaleString()} unique agents</div>
                    </div>
                  </div>
                  {/* API Key Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-zinc-900 border border-purple-500/20 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-4 h-4 text-purple-400" />
                        <span className="text-xs font-medium text-purple-400">API Key Requests (Total)</span>
                      </div>
                      <div className="text-xl font-bold text-white">{(stats.m2m_traffic?.api_key_requests_total || 0).toLocaleString()}</div>
                      <div className="text-xs mt-1 text-zinc-500">Authenticated agent requests</div>
                    </div>
                    <div className="bg-zinc-900 border border-purple-500/20 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-4 h-4 text-purple-400" />
                        <span className="text-xs font-medium text-purple-400">API Key Requests (Today)</span>
                      </div>
                      <div className="text-xl font-bold text-white">{(stats.m2m_traffic?.api_key_requests_today || 0).toLocaleString()}</div>
                      <div className="text-xs mt-1 text-zinc-500">Authenticated today</div>
                    </div>
                  </div>
                </div>

                {/* STXOSHI Token Section */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-yellow-400" />
                    STXOSHI Token Stats
                  </h3>
                  
                  {/* Price & Market Cap Row */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                    <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-4 h-4 text-yellow-400" />
                        <span className="text-xs font-medium text-yellow-400">STXOSHI Price</span>
                      </div>
                      <div className="text-lg sm:text-xl font-bold text-white">${stats.stxoshi.price_usd.toFixed(10)}</div>
                      <div className="text-xs mt-1 text-yellow-300/70">Live from ALEX DEX</div>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-yellow-400" />
                        <span className="text-xs font-medium text-yellow-400">Total Supply</span>
                      </div>
                      <div className="text-2xl font-bold text-white">{(stats.stxoshi.total_supply / 1_000_000_000).toFixed(0)}B</div>
                      <div className="text-xs mt-1 text-yellow-300/70">1 Billion coins</div>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <PieChart className="w-4 h-4 text-yellow-400" />
                        <span className="text-xs font-medium text-yellow-400">Market Cap</span>
                      </div>
                      <div className="text-2xl font-bold text-white">${stats.stxoshi.market_cap_usd.toLocaleString()}</div>
                      <div className="text-xs mt-1 text-yellow-300/70">Total value @ current price</div>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Activity className="w-4 h-4 text-yellow-400" />
                        <span className="text-xs font-medium text-yellow-400">24h Volume</span>
                      </div>
                      <div className="text-2xl font-bold text-white">{stats.stxoshi.volume_24h.toLocaleString()}</div>
                      <div className="text-xs mt-1 text-yellow-300/70">ALEX DEX</div>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Activity className="w-4 h-4 text-yellow-400" />
                        <span className="text-xs font-medium text-yellow-400">App TVL</span>
                      </div>
                      <div className="text-2xl font-bold text-white">{stats.stxoshi.tvl_coins.toLocaleString()}</div>
                      <div className="text-xs mt-1 text-yellow-300/70">${stats.stxoshi.tvl_usd.toFixed(2)} USD</div>
                    </div>
                  </div>
                  
                  {/* Committed & Paid Out Row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-blue-400" />
                        <span className="text-xs font-medium text-zinc-400">Committed (Open Positions)</span>
                      </div>
                      <div className="text-xl font-bold text-white">{stats.stxoshi.committed_coins.toLocaleString()}</div>
                      <div className="text-xs mt-1 text-zinc-500">${stats.stxoshi.committed_usd.toFixed(4)} USD</div>
                    </div>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        <span className="text-xs font-medium text-zinc-400">Pending Yield (25% APY)</span>
                      </div>
                      <div className="text-xl font-bold text-green-400">+{stats.stxoshi.pending_yield_coins.toLocaleString()}</div>
                      <div className="text-xs mt-1 text-zinc-500">${stats.stxoshi.pending_yield_usd.toFixed(4)} USD</div>
                    </div>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span className="text-xs font-medium text-zinc-400">Total Paid Out</span>
                      </div>
                      <div className="text-xl font-bold text-white">{stats.stxoshi.total_paid_coins.toLocaleString()}</div>
                      <div className="text-xs mt-1 text-zinc-500">${stats.stxoshi.total_paid_usd.toFixed(4)} USD</div>
                    </div>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-4 h-4 text-purple-400" />
                        <span className="text-xs font-medium text-zinc-400">Total Yield Paid</span>
                      </div>
                      <div className="text-xl font-bold text-purple-400">{stats.stxoshi.paid_yield_coins.toLocaleString()}</div>
                      <div className="text-xs mt-1 text-zinc-500">${(stats.stxoshi.paid_yield_coins * stats.stxoshi.price_usd).toFixed(4)} USD</div>
                    </div>
                  </div>
                </div>

                {/* Wallet Accounts Section */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    Wallet Accounts
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <StatCard
                      icon={Users}
                      label="Total Accounts"
                      value={stats.accounts.total}
                      color="blue"
                    />
                    <StatCard
                      icon={Activity}
                      label="Active Daily"
                      value={stats.accounts.active_daily}
                      subValue="Connected today"
                      color="green"
                    />
                    <StatCard
                      icon={Activity}
                      label="Active Weekly"
                      value={stats.accounts.active_weekly}
                      subValue="Connected this week"
                      color="green"
                    />
                    <StatCard
                      icon={Calendar}
                      label="New Today"
                      value={stats.accounts.new_today}
                      color="purple"
                    />
                    <StatCard
                      icon={Calendar}
                      label="New This Week"
                      value={stats.accounts.new_this_week}
                      color="purple"
                    />
                  </div>
                </div>

                {/* Agents Section */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Bot className="w-5 h-5 text-orange-400" />
                    Agents
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                    <StatCard
                      icon={Bot}
                      label="Total Agents"
                      value={stats.agents.total}
                      color="orange"
                    />
                    <StatCard
                      icon={Calendar}
                      label="New Today"
                      value={stats.agents.new_today}
                      color="yellow"
                    />
                    <StatCard
                      icon={Calendar}
                      label="New This Week"
                      value={stats.agents.new_this_week}
                      color="yellow"
                    />
                    <StatCard
                      icon={XCircle}
                      label="Total Closed"
                      value={stats.agents.total_closed}
                      color="pink"
                    />
                    <StatCard
                      icon={XCircle}
                      label="Closed Today"
                      value={stats.agents.closed_today}
                      color="pink"
                    />
                    <StatCard
                      icon={XCircle}
                      label="Closed This Week"
                      value={stats.agents.closed_this_week}
                      color="pink"
                    />
                  </div>
                </div>

                {/* Arbitrage Bots Section */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-cyan-400" />
                    Arbitrage Bots
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                    <StatCard
                      icon={Cpu}
                      label="Total Bots"
                      value={stats.bots?.total || 0}
                      color="blue"
                    />
                    <StatCard
                      icon={Calendar}
                      label="New Today"
                      value={stats.bots?.new_today || 0}
                      color="yellow"
                    />
                    <StatCard
                      icon={Calendar}
                      label="New This Week"
                      value={stats.bots?.new_this_week || 0}
                      color="yellow"
                    />
                    <StatCard
                      icon={Activity}
                      label="Running"
                      value={stats.bots?.running || 0}
                      subValue="Active now"
                      color="green"
                    />
                    <StatCard
                      icon={XCircle}
                      label="Stopped"
                      value={stats.bots?.stopped || 0}
                      color="pink"
                    />
                    <StatCard
                      icon={TrendingUp}
                      label="Total Trades"
                      value={stats.bots?.total_trades || 0}
                      color="purple"
                    />
                  </div>
                </div>

                {/* Flips Market Section */}
                {flipStats && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Coins className="w-5 h-5 text-orange-400" />
                      Flips Market
                    </h3>
                    
                    {/* Total Flips */}
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
                      <StatCard
                        icon={Coins}
                        label="Total Flips"
                        value={flipStats.flip_counts?.total_flips || 0}
                        color="orange"
                      />
                      <StatCard
                        icon={CheckCircle}
                        label="Total Correct"
                        value={flipStats.flip_counts?.correct?.total || 0}
                        color="green"
                      />
                      <StatCard
                        icon={XCircle}
                        label="Total Incorrect"
                        value={flipStats.flip_counts?.incorrect?.total || 0}
                        color="pink"
                      />
                      <StatCard
                        icon={Droplets}
                        label="Total LPs"
                        value={flipStats.lp_totals?.total_lps || 0}
                        color="blue"
                      />
                      <StatCard
                        icon={DollarSign}
                        label="LP Deposited"
                        value={`${((flipStats.lp_totals?.total_deposited || 0) / 1e6).toLocaleString()}`}
                        subValue="STXOSHI"
                        color="green"
                      />
                      <StatCard
                        icon={TrendingUp}
                        label="LP Net Profit"
                        value={`${((flipStats.lp_totals?.total_net_profit || 0) / 1e6).toLocaleString()}`}
                        subValue="STXOSHI"
                        color={flipStats.lp_totals?.total_net_profit >= 0 ? "green" : "pink"}
                      />
                    </div>
                    
                    {/* Correct Flips by Slot Range */}
                    <h4 className="text-sm font-medium text-green-400 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> Correct Flips by Slot
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                      <StatCard
                        icon={Target}
                        label="Correct 1-3"
                        value={flipStats.flip_counts?.correct?.slot_1_3 || 0}
                        color="green"
                      />
                      <StatCard
                        icon={Target}
                        label="Correct 4-6"
                        value={flipStats.flip_counts?.correct?.slot_4_6 || 0}
                        color="green"
                      />
                      <StatCard
                        icon={Target}
                        label="Correct 7-8"
                        value={flipStats.flip_counts?.correct?.slot_7_8 || 0}
                        color="green"
                      />
                      <StatCard
                        icon={Trophy}
                        label="Correct 9x"
                        value={flipStats.flip_counts?.correct?.slot_9 || 0}
                        subValue="512x payout"
                        color="yellow"
                      />
                      <StatCard
                        icon={Trophy}
                        label="Correct 10x"
                        value={flipStats.flip_counts?.correct?.slot_10 || 0}
                        subValue="1024x payout"
                        color="yellow"
                      />
                    </div>
                    
                    {/* Incorrect Flips by Slot Range */}
                    <h4 className="text-sm font-medium text-pink-400 mb-3 flex items-center gap-2">
                      <XCircle className="w-4 h-4" /> Incorrect Flips by Slot
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                      <StatCard
                        icon={XCircle}
                        label="Incorrect 1-3"
                        value={flipStats.flip_counts?.incorrect?.slot_1_3 || 0}
                        color="pink"
                      />
                      <StatCard
                        icon={XCircle}
                        label="Incorrect 4-6"
                        value={flipStats.flip_counts?.incorrect?.slot_4_6 || 0}
                        color="pink"
                      />
                      <StatCard
                        icon={XCircle}
                        label="Incorrect 7-8"
                        value={flipStats.flip_counts?.incorrect?.slot_7_8 || 0}
                        color="pink"
                      />
                      <StatCard
                        icon={XCircle}
                        label="Incorrect 9x"
                        value={flipStats.flip_counts?.incorrect?.slot_9 || 0}
                        color="pink"
                      />
                      <StatCard
                        icon={XCircle}
                        label="Incorrect 10x"
                        value={flipStats.flip_counts?.incorrect?.slot_10 || 0}
                        color="pink"
                      />
                    </div>
                    
                    {/* Win/Loss Amounts */}
                    <h4 className="text-sm font-medium text-blue-400 mb-3 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" /> Win/Loss Amounts
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <StatCard
                        icon={TrendingUp}
                        label="Avg Loss (→ LP)"
                        value={`${((flipStats.amounts?.avg_loss_stxoshi || 0) / 1e6).toFixed(2)}`}
                        subValue="STXOSHI"
                        color="green"
                      />
                      <StatCard
                        icon={TrendingUp}
                        label="Highest Loss (→ LP)"
                        value={`${((flipStats.amounts?.max_loss_stxoshi || 0) / 1e6).toLocaleString()}`}
                        subValue="STXOSHI"
                        color="green"
                      />
                      <StatCard
                        icon={ArrowUpRight}
                        label="Avg Win (← LP)"
                        value={`${((flipStats.amounts?.avg_win_stxoshi || 0) / 1e6).toFixed(2)}`}
                        subValue="STXOSHI"
                        color="pink"
                      />
                      <StatCard
                        icon={ArrowUpRight}
                        label="Highest Win (← LP)"
                        value={`${((flipStats.amounts?.max_win_stxoshi || 0) / 1e6).toLocaleString()}`}
                        subValue="STXOSHI"
                        color="pink"
                      />
                    </div>
                    
                    {/* LP Slots */}
                    <h4 className="text-sm font-medium text-cyan-400 mb-3 flex items-center gap-2">
                      <Droplets className="w-4 h-4" /> LP Slots (Top 3 LPs per Slot)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(slot => {
                        const slotData = flipStats.lp_slots?.[`slot_${slot}`];
                        return (
                          <div key={slot} className="rounded-xl border p-4 bg-cyan-500/10 border-cyan-500/30">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-cyan-400">Slot {slot}</span>
                              <span className="text-xs text-gray-400">{Math.pow(2, slot)}x</span>
                            </div>
                            <div className="text-lg font-bold text-white mb-2">
                              {((slotData?.total_liquidity || 0) / 1e6).toLocaleString()} <span className="text-xs text-gray-400">STXOSHI</span>
                            </div>
                            <div className="text-xs text-gray-400 mb-2">
                              W/L: {slotData?.win_count || 0}/{slotData?.loss_count || 0}
                            </div>
                            {slotData?.top_3_lps?.length > 0 ? (
                              <div className="space-y-1">
                                {slotData.top_3_lps.map((lp, idx) => (
                                  <div key={idx} className="flex justify-between text-xs">
                                    <span className="text-gray-400 font-mono">{lp.wallet_masked}</span>
                                    <span className="text-cyan-400">{lp.share_percentage?.toFixed(1)}%</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-500">No LPs</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Community Section */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-pink-400" />
                    Community
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <StatCard
                      icon={MessageSquare}
                      label="Total Posts"
                      value={stats.community.total_posts}
                      color="pink"
                    />
                    <StatCard
                      icon={Calendar}
                      label="New Posts Today"
                      value={stats.community.new_posts_today}
                      color="purple"
                    />
                    <StatCard
                      icon={Calendar}
                      label="New Posts This Week"
                      value={stats.community.new_posts_week}
                      color="purple"
                    />
                  </div>
                </div>

                {/* DeFi / TVL Section */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    DeFi & TVL
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard
                      icon={TrendingUp}
                      label="Total TVL"
                      value={`${stats.defi.total_tvl.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                      subValue={stats.defi.total_tvl_usd ? `$${stats.defi.total_tvl_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })} USD` : "STX/STXOSHI locked"}
                      color="green"
                    />
                    <StatCard
                      icon={DollarSign}
                      label="Total Yield Earned"
                      value={`${stats.defi.total_yield_earned.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                      subValue={stats.defi.stx_price ? `$${(stats.defi.total_yield_earned * stats.defi.stx_price).toLocaleString(undefined, { maximumFractionDigits: 2 })} USD` : "Across all positions"}
                      color="green"
                    />
                    <StatCard
                      icon={Activity}
                      label="Active Positions"
                      value={stats.defi.active_positions}
                      color="blue"
                    />
                    <StatCard
                      icon={Wallet}
                      label="Pending Withdrawals"
                      value={stats.withdrawals.pending}
                      subValue={`${stats.withdrawals.completed} completed`}
                      color="yellow"
                    />
                  </div>
                  
                  {/* TVL by Protocol */}
                  {Object.keys(stats.defi.tvl_by_protocol).length > 0 && (
                    <div className="mt-4 bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                      <h4 className="text-sm font-medium text-zinc-400 mb-3">TVL by Protocol</h4>
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                        {Object.entries(stats.defi.tvl_by_protocol).map(([protocol, tvlData]) => (
                          <div key={protocol} className="bg-zinc-800/50 rounded-lg p-3">
                            <div className="text-xs text-zinc-500 capitalize">{protocol}</div>
                            <div className="text-lg font-bold text-white">
                              {(tvlData.coins || tvlData).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </div>
                            {tvlData.usd !== undefined && (
                              <div className="text-xs text-green-400/70 mt-1">
                                ${tvlData.usd.toLocaleString(undefined, { maximumFractionDigits: 2 })} USD
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Activity Section */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-400" />
                    App Activity
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <StatCard
                      icon={Activity}
                      label="Total Activities"
                      value={stats.activity.total_activities}
                      color="blue"
                    />
                    <StatCard
                      icon={Calendar}
                      label="Activities Today"
                      value={stats.activity.activities_today}
                      color="green"
                    />
                    <StatCard
                      icon={Calendar}
                      label="Activities This Week"
                      value={stats.activity.activities_week}
                      color="green"
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-zinc-500">
                Failed to load statistics
              </div>
            )}
          </div>
        )}

        {/* Withdrawals Tab */}
        {activeTab === 'withdrawals' && (
          <>
            {/* Summary */}
            {summary && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                  <div className="text-zinc-400 text-sm">Pending Requests</div>
                  <div className="text-3xl font-bold text-yellow-400">{summary.pending_count}</div>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                  <div className="text-zinc-400 text-sm">Total Pending Amount</div>
                  <div className="text-3xl font-bold text-white">{summary.total_pending_stx.toFixed(4)} STXOSHI</div>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                  <div className="text-zinc-400 text-sm">Quick Actions</div>
                  {summary.pending_count > 0 ? (
                    <button
                      onClick={handleAutoFulfillAllWithdrawals}
                      disabled={processing === 'all'}
                      className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:from-zinc-600 disabled:to-zinc-600 rounded-lg text-black font-bold"
                    >
                      {processing === 'all' ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4" />
                          Auto-Fulfill All ({summary.pending_count})
                        </>
                      )}
                    </button>
                  ) : (
                    <div className="mt-2 text-green-400 flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      All caught up!
                    </div>
                  )}
                </div>
              </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {['pending', 'processing', 'completed', 'all'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg capitalize transition-colors ${
                filter === status
                  ? 'bg-orange-500 text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              {status}
            </button>
          ))}
        </div>

        {/* Withdrawals List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
          </div>
        ) : withdrawals.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <p className="text-zinc-400">No {filter} withdrawal requests</p>
          </div>
        ) : (
          <div className="space-y-4">
            {withdrawals.map((withdrawal) => (
              <div 
                key={withdrawal.id}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        withdrawal.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        withdrawal.status === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                        withdrawal.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        'bg-zinc-700 text-zinc-400'
                      }`}>
                        {withdrawal.status}
                      </span>
                      <span className="text-zinc-500 text-sm">
                        {new Date(withdrawal.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-white font-mono text-xs truncate">
                      To: {(withdrawal.user_stx_address || withdrawal.to_wallet || '').slice(0, 20)}...
                    </div>
                    {withdrawal.user_name && (
                      <div className="text-zinc-500 text-sm">
                        User: {withdrawal.user_name}
                      </div>
                    )}
                  </div>
                  
                  <div className="text-right flex-shrink-0">
                    <div className="text-lg md:text-2xl font-bold text-white">
                      {(withdrawal.amount / 1_000_000).toFixed(2)}
                    </div>
                    <div className="text-xs text-zinc-400">STXOSHI</div>
                    <div className="text-xs text-green-400">
                      +{((withdrawal.yield_amount || 0) / 1_000_000).toFixed(2)} yield
                    </div>
                  </div>
                </div>

                {/* Position Details */}
                {withdrawal.positions && withdrawal.positions.length > 0 && (
                  <div className="bg-zinc-800/50 rounded-lg p-3 mb-4">
                    <div className="text-xs text-zinc-500 mb-2">Position Details:</div>
                    {withdrawal.positions.map((pos, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-zinc-400">{pos.protocol} ({pos.asset})</span>
                        <span className="text-white">
                          {(pos.deposited / 1_000_000).toFixed(4)} + {(pos.yield / 1_000_000).toFixed(6)} yield
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-between">
                  <div className="text-xs text-zinc-600">
                    ID: {withdrawal.id.slice(0, 8)}...
                  </div>
                  
                  {withdrawal.status === 'pending' && (
                    <div className="flex items-center gap-2">
                      {/* Auto-Fulfill Button (recommended - no wallet required) */}
                      <button
                        onClick={() => handleAutoFulfillWithdrawal(withdrawal)}
                        disabled={processing === withdrawal.id}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:from-zinc-600 disabled:to-zinc-600 rounded-lg text-black font-bold transition-colors"
                        title="Auto-fulfill using Python backend (no wallet signing required)"
                      >
                        {processing === withdrawal.id ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Zap className="w-4 h-4" />
                            Auto-Send
                          </>
                        )}
                      </button>
                      
                      {/* Manual Sign Button (requires VWN wallet) */}
                      <button
                        onClick={() => handleProcessWithdrawal(withdrawal)}
                        disabled={processing === withdrawal.id || !isTreasury}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors ${
                          isTreasury 
                            ? 'bg-zinc-700 hover:bg-zinc-600 text-white' 
                            : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                        } disabled:bg-zinc-800`}
                        title={!isTreasury ? 'Connect with treasury wallet (VWN) to sign manually' : 'Sign manually with VWN wallet'}
                      >
                        <Wallet className="w-4 h-4" />
                        Manual
                      </button>
                    </div>
                  )}
                  
                  {withdrawal.status === 'completed' && withdrawal.process_txid && (
                    <a
                      href={`https://explorer.hiro.so/txid/${withdrawal.process_txid}?chain=mainnet`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View TX
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}

        {/* Recovery Tool Tab */}
        {activeTab === 'recovery' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-orange-400" />
                <div>
                  <h3 className="text-lg font-bold text-white">Deposit Recovery Tool</h3>
                  <p className="text-sm text-zinc-400">Recover deposits that succeeded on-chain but weren't recorded by the app</p>
                </div>
              </div>
              
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-6">
                <p className="text-sm text-orange-200">
                  <strong>⚠️ Use with caution:</strong> This tool manually creates deposit positions. 
                  Always verify the transaction on-chain before recovery.
                </p>
              </div>

              {/* Step 1: Verify Transaction */}
              <div className="mb-6">
                <h4 className="text-md font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded">Step 1</span>
                  Verify Transaction On-Chain
                </h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={recoveryTxid}
                    onChange={(e) => setRecoveryTxid(e.target.value)}
                    placeholder="Enter transaction ID (0x...)"
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:border-orange-500"
                  />
                  <button
                    onClick={verifyTransaction}
                    disabled={recoveryLoading || !recoveryTxid.trim()}
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg text-white font-medium flex items-center gap-2"
                  >
                    {recoveryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    Verify
                  </button>
                </div>
                
                {/* Transaction Verification Result */}
                {txVerification && (
                  <div className={`mt-4 p-4 rounded-lg border ${
                    txVerification.found && txVerification.status === 'success'
                      ? 'bg-green-500/10 border-green-500/30'
                      : 'bg-red-500/10 border-red-500/30'
                  }`}>
                    {txVerification.found ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          {txVerification.status === 'success' ? (
                            <CheckCircle className="w-5 h-5 text-green-400" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400" />
                          )}
                          <span className={`font-medium ${txVerification.status === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                            Transaction {txVerification.status === 'success' ? 'Verified' : 'Status: ' + txVerification.status}
                          </span>
                        </div>
                        <div className="text-sm text-zinc-300 space-y-1">
                          <p><span className="text-zinc-500">Type:</span> {txVerification.tx_type}</p>
                          <p><span className="text-zinc-500">Sender:</span> {txVerification.sender_address}</p>
                          {txVerification.block_height && (
                            <p><span className="text-zinc-500">Block:</span> {txVerification.block_height}</p>
                          )}
                          {txVerification.burn_block_time_iso && (
                            <p><span className="text-zinc-500">Time:</span> {new Date(txVerification.burn_block_time_iso).toLocaleString()}</p>
                          )}
                        </div>
                        <a
                          href={txVerification.explorer_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                        >
                          <ExternalLink className="w-4 h-4" />
                          View on Explorer
                        </a>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <XCircle className="w-5 h-5 text-red-400" />
                        <span className="text-red-400">{txVerification.message || 'Transaction not found'}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Step 2: Look Up Wallet */}
              <div className="mb-6">
                <h4 className="text-md font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded">Step 2</span>
                  Look Up User's Wallet & Agent
                </h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={lookupAddress}
                    onChange={(e) => setLookupAddress(e.target.value)}
                    placeholder="Enter STX wallet address (SP...)"
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:border-orange-500"
                  />
                  <button
                    onClick={lookupAgent}
                    disabled={lookupLoading || !lookupAddress.trim()}
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg text-white font-medium flex items-center gap-2"
                  >
                    {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    Lookup
                  </button>
                </div>
                
                {/* Lookup Result */}
                {lookupResult && lookupResult.found && (
                  <div className="mt-4 p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                    <p className="text-sm text-zinc-400 mb-3">
                      User: <span className="text-white">{lookupResult.user.username || lookupResult.user.stx_address.slice(0, 12) + '...'}</span>
                    </p>
                    
                    <p className="text-sm text-zinc-400 mb-2">Select an agent:</p>
                    <div className="space-y-2">
                      {lookupResult.agents.map(agent => (
                        <div
                          key={agent.id}
                          onClick={() => setSelectedAgent(agent)}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedAgent?.id === agent.id
                              ? 'bg-orange-500/20 border-orange-500'
                              : 'bg-zinc-900 border-zinc-700 hover:border-zinc-600'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-white">{agent.name}</p>
                              <p className="text-xs text-zinc-500">ID: {agent.id.slice(0, 8)}...</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-zinc-400">{agent.positions_count || 0} active positions</p>
                              <p className="text-xs text-zinc-500">Created: {new Date(agent.created_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {lookupResult.agents.length === 0 && (
                      <p className="text-yellow-400 text-sm">No agents found for this wallet</p>
                    )}
                    
                    {/* Manual Agent ID or Name Entry */}
                    <div className="mt-4 pt-4 border-t border-zinc-700">
                      <p className="text-xs text-zinc-500 mb-2">Or enter Agent ID or Name:</p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={manualAgentId}
                          onChange={(e) => setManualAgentId(e.target.value)}
                          placeholder="Agent ID or Name (e.g. SD, e02bd8ed...)"
                          className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
                        />
                        <button
                          onClick={async () => {
                            const input = manualAgentId.trim();
                            if (!input) return;
                            
                            // Check if it looks like a UUID (contains dashes and is long)
                            const isUUID = input.includes('-') && input.length > 20;
                            
                            if (isUUID) {
                              // Use directly as ID
                              setSelectedAgent({
                                id: input,
                                name: `Agent ${input.slice(0, 8)}...`,
                                positions_count: 0
                              });
                            } else {
                              // Search by name
                              try {
                                const response = await fetch(
                                  `${API}/admin/search-agent?name=${encodeURIComponent(input)}&user_address=${lookupAddress}`,
                                  { headers: { 'X-STX-Address': addresses?.stx } }
                                );
                                if (response.ok) {
                                  const data = await response.json();
                                  if (data.agent) {
                                    setSelectedAgent({
                                      id: data.agent.id,
                                      name: data.agent.name,
                                      positions_count: data.agent.positions_count || 0
                                    });
                                    toast.success(`Found agent: ${data.agent.name}`);
                                  } else {
                                    toast.error('Agent not found by name');
                                  }
                                } else {
                                  // Fallback - use as ID anyway
                                  setSelectedAgent({
                                    id: input,
                                    name: input,
                                    positions_count: 0
                                  });
                                }
                              } catch (e) {
                                // Fallback - use as ID
                                setSelectedAgent({
                                  id: input,
                                  name: input,
                                  positions_count: 0
                                });
                              }
                            }
                          }}
                          className="px-4 py-2 bg-orange-600 hover:bg-orange-500 rounded-lg text-white text-sm font-medium"
                        >
                          Search
                        </button>
                      </div>
                      <p className="text-xs text-zinc-600 mt-1">Enter agent name like "SD" or full UUID</p>
                    </div>
                    
                    {/* Show positions for selected agent with bonus update option */}
                    {selectedAgent && selectedAgent.positions && selectedAgent.positions.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-zinc-700">
                        <h5 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-green-400" />
                          Update Bonus on Existing Position
                        </h5>
                        <p className="text-xs text-zinc-500 mb-3">
                          If the position already exists, add bonus yield here instead of creating a new one.
                        </p>
                        
                        <div className="space-y-2 mb-3">
                          {selectedAgent.positions.map(pos => (
                            <div
                              key={pos.id}
                              onClick={() => setSelectedPosition(pos)}
                              className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                selectedPosition?.id === pos.id
                                  ? 'bg-green-500/20 border-green-500'
                                  : 'bg-zinc-900 border-zinc-700 hover:border-zinc-600'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-white">{pos.protocol?.toUpperCase()} - {pos.asset?.toUpperCase()}</p>
                                  <p className="text-xs text-zinc-500">Deposited: {(pos.amount_deposited / 1_000_000).toLocaleString()}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm text-green-400">+{((pos.extra_yield_bonus || 0) / 1_000_000).toLocaleString()} bonus</p>
                                  <p className="text-xs text-zinc-500">{pos.status}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {selectedPosition && (
                          <>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              value={bonusYieldAmount}
                              onChange={(e) => setBonusYieldAmount(e.target.value)}
                              placeholder="e.g., 1000 or -500"
                              className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:border-green-500"
                            />
                            <button
                              onClick={updateBonusYield}
                              disabled={bonusUpdateLoading || !bonusYieldAmount}
                              className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg text-white font-medium flex items-center gap-2"
                            >
                              {bonusUpdateLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
                              Adjust
                            </button>
                          </div>
                          <p className="text-xs text-zinc-500 mt-1">Use negative to reduce (e.g., -1000)</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Step 3: Enter Amount & Execute */}
              <div className="mb-6">
                <h4 className="text-md font-semibold text-white mb-3 flex items-center gap-2">
                  <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded">Step 3</span>
                  Enter Amount & Execute Recovery
                </h4>
                
                {/* Protocol Selector */}
                <div className="mb-4">
                  <label className="block text-sm text-zinc-400 mb-1">Protocol</label>
                  <select
                    value={recoveryProtocol}
                    onChange={(e) => setRecoveryProtocol(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-orange-500"
                  >
                    {PROTOCOLS.map(p => (
                      <option key={p.value} value={p.value}>
                        {p.label} ({p.asset.toUpperCase()}) - {p.apy}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">
                      Amount ({PROTOCOLS.find(p => p.value === recoveryProtocol)?.asset.toUpperCase() || 'coins'})
                    </label>
                    <input
                      type="number"
                      value={recoveryAmount}
                      onChange={(e) => setRecoveryAmount(e.target.value)}
                      placeholder="e.g., 10000000 for 10M"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Yield Adjustment (optional)</label>
                    <input
                      type="number"
                      value={extraYield}
                      onChange={(e) => setExtraYield(e.target.value)}
                      placeholder="e.g., 429 or -1000"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:border-green-500"
                    />
                    <p className="text-xs text-zinc-500 mt-1">Use positive to add, negative to reduce (e.g., -1000)</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Notes (optional)</label>
                    <input
                      type="text"
                      value={recoveryNotes}
                      onChange={(e) => setRecoveryNotes(e.target.value)}
                      placeholder="Recovery reason/notes"
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:border-orange-500"
                    />
                  </div>
                  {txVerification?.burn_block_time_iso && (
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Original TX Time</label>
                      <div className="w-full bg-zinc-900 border border-green-500/30 rounded-lg px-4 py-2 text-green-400 text-sm">
                        {new Date(txVerification.burn_block_time_iso).toLocaleString()}
                        <p className="text-xs text-zinc-500 mt-1">Yield calculated from this date</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Summary */}
                {selectedAgent && recoveryAmount && (
                  <div className="mt-4 p-4 bg-zinc-900 border border-zinc-700 rounded-lg">
                    <h5 className="text-sm font-medium text-white mb-2">Recovery Summary</h5>
                    <div className="text-sm text-zinc-400 space-y-1">
                      <p><span className="text-zinc-500">Transaction:</span> {recoveryTxid.slice(0, 20)}...</p>
                      <p><span className="text-zinc-500">Agent:</span> {selectedAgent.name}</p>
                      <p><span className="text-zinc-500">Protocol:</span> {PROTOCOLS.find(p => p.value === recoveryProtocol)?.label} ({PROTOCOLS.find(p => p.value === recoveryProtocol)?.apy})</p>
                      <p><span className="text-zinc-500">Amount:</span> {parseFloat(recoveryAmount).toLocaleString()} {PROTOCOLS.find(p => p.value === recoveryProtocol)?.asset.toUpperCase()}</p>
                      {extraYield && parseFloat(extraYield) !== 0 && (
                        <p>
                          <span className="text-zinc-500">Yield Adjustment:</span> 
                          <span className={parseFloat(extraYield) > 0 ? 'text-green-400' : 'text-red-400'}>
                            {parseFloat(extraYield) > 0 ? '+' : ''}{parseFloat(extraYield).toLocaleString()}
                          </span>
                        </p>
                      )}
                      {txVerification?.burn_block_time_iso && (
                        <p><span className="text-zinc-500">Yield Start Date:</span> <span className="text-green-400">{new Date(txVerification.burn_block_time_iso).toLocaleDateString()}</span></p>
                      )}
                    </div>
                  </div>
                )}

                <button
                  onClick={executeRecovery}
                  disabled={recoveryLoading || !recoveryTxid || !selectedAgent || !recoveryAmount}
                  className="mt-4 w-full px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 disabled:from-zinc-700 disabled:to-zinc-700 disabled:text-zinc-500 rounded-lg text-white font-bold flex items-center justify-center gap-2"
                >
                  {recoveryLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing Recovery...
                    </>
                  ) : (
                    <>
                      <Wrench className="w-5 h-5" />
                      Execute Recovery
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Swaps Fulfillment Tab */}
        {activeTab === 'swaps' && (
          <div className="space-y-6">
            {/* Hot Wallet Status */}
            {hotWalletBalance && (
              <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-6">
                <h3 className="text-lg font-bold text-amber-400 mb-4 flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Hot Wallet Status
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-zinc-900/50 rounded-lg p-4">
                    <div className="text-sm text-zinc-400">Address</div>
                    <div className="font-mono text-xs text-amber-300 break-all">
                      {hotWalletBalance.hot_wallet_address?.slice(0, 10)}...{hotWalletBalance.hot_wallet_address?.slice(-6)}
                    </div>
                  </div>
                  <div className="bg-zinc-900/50 rounded-lg p-4">
                    <div className="text-sm text-zinc-400">STXOSHI Balance</div>
                    <div className="text-xl font-bold text-amber-400">
                      {hotWalletBalance.balances?.stxoshi?.toLocaleString() || 0}
                    </div>
                  </div>
                  <div className="bg-zinc-900/50 rounded-lg p-4">
                    <div className="text-sm text-zinc-400">STX Balance</div>
                    <div className="text-xl font-bold text-white">
                      {hotWalletBalance.balances?.stx?.toFixed(4) || 0}
                    </div>
                  </div>
                  <div className="bg-zinc-900/50 rounded-lg p-4">
                    <div className="text-sm text-zinc-400">Pending Fulfillment</div>
                    <div className="text-xl font-bold text-yellow-400">
                      {hotWalletBalance.pending_fulfillment?.total_stxoshi?.toLocaleString() || 0}
                    </div>
                  </div>
                </div>
                {!hotWalletBalance.can_fulfill_all && hotWalletBalance.pending_fulfillment?.count > 0 && (
                  <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <span className="text-red-300 text-sm">
                      Insufficient STXOSHI balance to fulfill all pending swaps. Need {Math.abs(hotWalletBalance.available_after_pending)?.toLocaleString()} more.
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Swap Stats */}
            {swapStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                  <div className="text-sm text-yellow-400">Pending</div>
                  <div className="text-2xl font-bold text-yellow-300">{swapStats.pending || 0}</div>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                  <div className="text-sm text-blue-400">Awaiting Fulfillment</div>
                  <div className="text-2xl font-bold text-blue-300">{swapStats.deposited || 0}</div>
                </div>
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                  <div className="text-sm text-green-400">Fulfilled</div>
                  <div className="text-2xl font-bold text-green-300">{swapStats.fulfilled || 0}</div>
                </div>
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                  <div className="text-sm text-purple-400">Total STX Received</div>
                  <div className="text-2xl font-bold text-purple-300">{swapStats.total_stx_received?.toFixed(2) || 0}</div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={fetchSwaps}
                disabled={swapsLoading}
                className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white flex items-center justify-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${swapsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              
              <select
                value={swapFilter}
                onChange={(e) => setSwapFilter(e.target.value)}
                className="px-4 py-2 bg-zinc-700 rounded-lg text-white flex-1 sm:flex-none"
              >
                <option value="deposited">Awaiting Fulfillment</option>
                <option value="pending">Pending Deposit</option>
                <option value="fulfilled">Fulfilled</option>
                <option value="cancelled">Cancelled</option>
              </select>
              
              {swapFilter === 'deposited' && swaps.length > 0 && (
                <button
                  onClick={fulfillAllSwaps}
                  disabled={swapFulfilling || !hotWalletBalance?.can_fulfill_all}
                  className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:from-zinc-600 disabled:to-zinc-600 rounded-lg text-black font-bold flex items-center justify-center gap-2"
                >
                  {swapFulfilling === 'all' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Fulfilling...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      Auto-Fulfill ({swaps.length})
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Abandoned Swaps Cleanup Tool */}
            <div className="bg-zinc-900/50 border border-zinc-700 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  <h4 className="text-md font-semibold text-white">Abandoned Pending Swaps</h4>
                </div>
                <button
                  onClick={fetchAbandonedSwaps}
                  disabled={abandonedLoading}
                  className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-sm text-white flex items-center gap-2"
                >
                  {abandonedLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  Check
                </button>
              </div>
              <p className="text-sm text-zinc-400 mb-3">
                Find and cancel swaps where users initiated but never deposited STX (older than 24 hours).
              </p>
              
              {abandonedSwaps && (
                <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <div className="text-xs text-zinc-500">Count</div>
                      <div className="text-lg font-bold text-yellow-400">{abandonedSwaps.count}</div>
                    </div>
                    <div>
                      <div className="text-xs text-zinc-500">STX (would have been)</div>
                      <div className="text-lg font-bold text-white">{abandonedSwaps.total_stx_would_have_been?.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-zinc-500">STXOSHI (would have been)</div>
                      <div className="text-lg font-bold text-white">{abandonedSwaps.total_stxoshi_would_have_been?.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-zinc-500">Cutoff</div>
                      <div className="text-sm text-zinc-300">{new Date(abandonedSwaps.cutoff_time).toLocaleString()}</div>
                    </div>
                  </div>
                  
                  {abandonedSwaps.count > 0 && (
                    <>
                      <div className="max-h-40 overflow-y-auto bg-zinc-900/50 rounded-lg p-2">
                        {abandonedSwaps.swaps?.slice(0, 10).map((swap, idx) => (
                          <div key={swap.id} className="flex justify-between text-xs py-1 border-b border-zinc-800 last:border-0">
                            <span className="text-zinc-400 font-mono">{swap.user_address?.slice(0, 10)}...</span>
                            <span className="text-white">{swap.stx_amount} STX</span>
                            <span className="text-zinc-500">{new Date(swap.created_at).toLocaleDateString()}</span>
                          </div>
                        ))}
                        {abandonedSwaps.count > 10 && (
                          <div className="text-xs text-zinc-500 text-center py-1">
                            ... and {abandonedSwaps.count - 10} more
                          </div>
                        )}
                      </div>
                      
                      <button
                        onClick={cancelAbandonedSwaps}
                        disabled={cancellingAbandoned}
                        className="w-full px-4 py-2 bg-red-600 hover:bg-red-500 disabled:bg-zinc-700 rounded-lg text-white font-medium flex items-center justify-center gap-2"
                      >
                        {cancellingAbandoned ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Cancelling...
                          </>
                        ) : (
                          <>
                            <X className="w-4 h-4" />
                            Cancel All {abandonedSwaps.count} Abandoned Swaps
                          </>
                        )}
                      </button>
                    </>
                  )}
                  
                  {abandonedSwaps.count === 0 && (
                    <div className="text-center py-2 text-green-400 flex items-center justify-center gap-2">
                      <Check className="w-4 h-4" />
                      No abandoned pending swaps found
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Swaps List - Mobile Cards + Desktop Table */}
            <div className="bg-zinc-800/50 rounded-xl border border-zinc-700 overflow-hidden">
              {/* Desktop Table - Hidden on mobile */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-900/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">User</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">STX In</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">STXOSHI Out</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Created</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-700">
                    {swapsLoading ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" />
                        </td>
                      </tr>
                    ) : swaps.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-zinc-500">
                          No swaps found
                        </td>
                      </tr>
                    ) : (
                      swaps.map((swap) => (
                        <tr key={swap.id} className="hover:bg-zinc-800/50">
                          <td className="px-4 py-3">
                            <div className="font-mono text-xs text-zinc-300">
                              {swap.user_address?.slice(0, 8)}...{swap.user_address?.slice(-6)}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-bold text-white">{swap.stx_amount} STX</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-bold text-amber-400">{swap.stxoshi_amount?.toLocaleString()}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              swap.status === 'fulfilled' ? 'bg-green-500/20 text-green-400' :
                              swap.status === 'deposited' ? 'bg-blue-500/20 text-blue-400' :
                              swap.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {swap.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-zinc-400">
                            {new Date(swap.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2 flex-wrap">
                              {swap.status === 'deposited' && (
                                <>
                                  <button
                                    onClick={() => fulfillSwap(swap.id)}
                                    disabled={swapFulfilling === swap.id}
                                    className="px-2 py-1 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-600 rounded text-black text-xs font-bold flex items-center gap-1"
                                  >
                                    {swapFulfilling === swap.id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Zap className="w-3 h-3" />
                                    )}
                                    Send
                                  </button>
                                  <button
                                    onClick={() => markSwapComplete(swap.id, swap.stxoshi_amount)}
                                    disabled={swapFulfilling === swap.id}
                                    className="px-2 py-1 bg-green-600 hover:bg-green-500 disabled:bg-zinc-600 rounded text-white text-xs flex items-center gap-1"
                                  >
                                    <Check className="w-3 h-3" />
                                    Paid
                                  </button>
                                  <button
                                    onClick={() => cancelSwap(swap.id)}
                                    disabled={swapFulfilling === swap.id}
                                    className="px-2 py-1 bg-red-600 hover:bg-red-500 disabled:bg-zinc-600 rounded text-white text-xs flex items-center gap-1"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </>
                              )}
                              {swap.status === 'pending' && (
                                <button
                                  onClick={() => cancelSwap(swap.id)}
                                  className="px-2 py-1 bg-red-600 hover:bg-red-500 rounded text-white text-xs flex items-center gap-1"
                                >
                                  <X className="w-3 h-3" />
                                  Cancel
                                </button>
                              )}
                              {swap.deposit_txid && swap.deposit_txid !== 'manual' && (
                                <a
                                  href={`https://explorer.hiro.so/txid/${swap.deposit_txid}?chain=mainnet`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 rounded text-white text-xs flex items-center gap-1"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  In
                                </a>
                              )}
                              {swap.payout_txid && swap.payout_txid !== 'manual' && (
                                <a
                                  href={`https://explorer.hiro.so/txid/${swap.payout_txid}?chain=mainnet`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-2 py-1 bg-green-700 hover:bg-green-600 rounded text-white text-xs flex items-center gap-1"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Out
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Mobile Cards - Visible only on mobile */}
              <div className="md:hidden">
                {swapsLoading ? (
                  <div className="px-4 py-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" />
                  </div>
                ) : swaps.length === 0 ? (
                  <div className="px-4 py-8 text-center text-zinc-500">
                    No swaps found
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-700">
                    {swaps.map((swap) => (
                      <div key={swap.id} className="p-4 space-y-3">
                        {/* Header: User + Status */}
                        <div className="flex items-center justify-between">
                          <div className="font-mono text-xs text-zinc-300">
                            {swap.user_address?.slice(0, 10)}...{swap.user_address?.slice(-4)}
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            swap.status === 'fulfilled' ? 'bg-green-500/20 text-green-400' :
                            swap.status === 'deposited' ? 'bg-blue-500/20 text-blue-400' :
                            swap.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {swap.status}
                          </span>
                        </div>
                        
                        {/* Amounts */}
                        <div className="flex items-center justify-between bg-zinc-900/50 rounded-lg p-3">
                          <div>
                            <div className="text-xs text-zinc-500">STX In</div>
                            <div className="font-bold text-white">{swap.stx_amount}</div>
                          </div>
                          <ArrowUpRight className="w-4 h-4 text-zinc-600" />
                          <div className="text-right">
                            <div className="text-xs text-zinc-500">STXOSHI Out</div>
                            <div className="font-bold text-amber-400">{swap.stxoshi_amount?.toLocaleString()}</div>
                          </div>
                        </div>
                        
                        {/* Date */}
                        <div className="text-xs text-zinc-500">
                          Created: {new Date(swap.created_at).toLocaleDateString()}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-2 flex-wrap">
                          {swap.status === 'deposited' && (
                            <>
                              <button
                                onClick={() => fulfillSwap(swap.id)}
                                disabled={swapFulfilling === swap.id}
                                className="flex-1 px-3 py-2 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-600 rounded-lg text-black text-sm font-bold flex items-center justify-center gap-1"
                              >
                                {swapFulfilling === swap.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Zap className="w-4 h-4" />
                                )}
                                Send
                              </button>
                              <button
                                onClick={() => markSwapComplete(swap.id, swap.stxoshi_amount)}
                                disabled={swapFulfilling === swap.id}
                                className="px-3 py-2 bg-green-600 hover:bg-green-500 disabled:bg-zinc-600 rounded-lg text-white text-sm flex items-center gap-1"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => cancelSwap(swap.id)}
                                disabled={swapFulfilling === swap.id}
                                className="px-3 py-2 bg-red-600 hover:bg-red-500 disabled:bg-zinc-600 rounded-lg text-white text-sm flex items-center gap-1"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {swap.status === 'pending' && (
                            <button
                              onClick={() => cancelSwap(swap.id)}
                              className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-500 rounded-lg text-white text-sm flex items-center justify-center gap-1"
                            >
                              <X className="w-4 h-4" />
                              Cancel Swap
                            </button>
                          )}
                          {swap.deposit_txid && swap.deposit_txid !== 'manual' && (
                            <a
                              href={`https://explorer.hiro.so/txid/${swap.deposit_txid}?chain=mainnet`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white text-sm flex items-center gap-1"
                            >
                              <ExternalLink className="w-4 h-4" />
                              TX In
                            </a>
                          )}
                          {swap.payout_txid && swap.payout_txid !== 'manual' && (
                            <a
                              href={`https://explorer.hiro.so/txid/${swap.payout_txid}?chain=mainnet`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-2 bg-green-700 hover:bg-green-600 rounded-lg text-white text-sm flex items-center gap-1"
                            >
                              <ExternalLink className="w-4 h-4" />
                              TX Out
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
